import {
  CLASIF_A_TIPO_ALERTA,
  type AlertaBajaTipo,
  type PreviewBajaBinItem,
} from '@lib/services/bajas.service';

/**
 * Reglas de la revisión de bajas: qué se puede editar, qué se puede confirmar,
 * qué hay que corregir antes de seguir y qué queda registrado como alerta.
 *
 * Viven acá y no en `pages/Bajas.tsx` porque son lógica pura y se testean sin
 * renderizar la pantalla (además de que el archivo de la página solo puede
 * exportar componentes).
 */

/**
 * Motivos HABILITADOS (catálogo Binary SA_TIPOS_BAJA_AFI).
 *
 * Son solo estos dos por definición del negocio para esta etapa, confirmada por
 * el DBA el 2026-08-04. Antes la lista incluía 31020 / 33150 / 35030, que no
 * están en el alcance. El backend tiene la MISMA allowlist (`MOTIVOS_HABILITADOS`
 * en `bajas-binary.service.ts`) y rechaza cualquier otro código: esta lista es
 * la comodidad de la pantalla, la del server es la que manda — el archivo puede
 * traer una columna MOTIVO con cualquier cosa.
 */
export const MOTIVOS: Array<{ codigo: string; label: string }> = [
  { codigo: '36010', label: 'Baja pedida por empresa' },
  { codigo: '37020', label: 'Fallecimiento' },
];
export const motivoLabel = (c: string): string => MOTIVOS.find((m) => m.codigo === c)?.label ?? c;

/** Motivo "Fallecimiento": exige la fecha REAL del deceso (se registra como FFAL). */
export const TB_FALLECIMIENTO = '37020';
/** Motivo "Baja pedida por empresa": el server fuerza la vigencia al 1° del mes próximo. */
export const TB_EMPRESA = '36010';

/** 1° del mes próximo en dd/mm/aaaa — la vigencia que el server aplica al 36010. */
export const VIGENCIA_EMPRESA = (() => {
  const n = new Date();
  const d = new Date(n.getFullYear(), n.getMonth() + 1, 1);
  return `01/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
})();

/** Fila en la revisión: el item del preview + motivo editable + selección. */
export interface FilaRev {
  item: PreviewBajaBinItem;
  /** Motivo elegido (código). Editable en la revisión. */
  tb: string;
  /** Vigencia en ISO (yyyy-mm-dd) para el <input type="date">. Editable. */
  vig: string;
  /** Fecha real del deceso en ISO. Editable; solo aplica al motivo 37020. */
  fall: string;
  sel: boolean;
  /**
   * El rearmado de grupo PLANIFICADO para esta fila.
   *
   * No es un dato del archivo ni del preview: es una decisión que se toma en la
   * revisión. No se ejecuta al armarlo — viaja con el lote y corre al confirmar,
   * igual que el resto de las filas. Lo único que cambia de entrada es que la
   * fila deja de ir a Alertas Bajas: ya no hay nada que gestionar a mano.
   */
  rearmado?: PlanRearmado;
}

/** El reparto decidido para una fila, tal como se manda en el lote. */
export interface PlanRearmado {
  grupos: Array<{ titularAfiId: string; integrantesAfiIds: string[] }>;
  /** Los que no siguen con nadie: se dan de baja junto con el titular. */
  dadosDeBaja: string[];
  tb: string;
  vig: string;
  fall: string;
  /** Cómo queda el grupo, en una frase, para la fila y para el paso de confirmar. */
  resumen: string;
}

/**
 * dd/mm/aaaa → yyyy-mm-dd. '' si no es una fecha REAL del calendario.
 *
 * Valida el día contra el mes (31/02 no existe) además del formato: el estado
 * de la fila se guarda en ISO y todo lo que sale de acá viaja a Binary, así que
 * una fecha imposible tiene que quedar afuera y hacer que la fila se marque
 * para corregir, no colarse.
 */
export function toISO(ddmmyyyy: string | undefined): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((ddmmyyyy ?? '').trim());
  if (!m) return '';
  const [, dd, mm, aaaa] = m;
  const d = Number(dd);
  const mes = Number(mm);
  const a = Number(aaaa);
  if (mes < 1 || mes > 12 || d < 1) return '';
  // Día 0 del mes siguiente = último día de este mes (28/29/30/31 según el año).
  const ultimoDia = new Date(a, mes, 0).getDate();
  if (d > ultimoDia) return '';
  return `${aaaa}-${mm}-${dd}`;
}

/**
 * Formatea lo que se va tipeando como dd/mm/aaaa: deja solo dígitos, corta en 8
 * y mete las barras sola. Sirve para escribir la fecha en formato argentino sin
 * depender del idioma del navegador — el `<input type="date">` nativo se dibuja
 * según la configuración de la máquina y en una en inglés muestra mm/dd/aaaa.
 */
export function enmascararFecha(txt: string): string {
  const d = (txt || '').replace(/\D/g, '').slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
}

/** yyyy-mm-dd → dd/mm/aaaa (formato que espera Binary). */
export function toDDMMYYYY(iso: string): string {
  const [y, m, d] = (iso || '').split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/** Clasificaciones bajables (seleccionables + motivo editable): el integrante
 *  familiar y la baja del LEGAJO COMPLETO, sea titular + todos sus familiares
 *  (`grupo_completo`) o titular sin grupo (`titular_solo`, el legajo es él solo).
 *  Lo único bloqueado es el titular con familia activa parcial (`titular`), que
 *  requiere reasignar el grupo. */
export const esEditable = (c: PreviewBajaBinItem['clasif']): boolean =>
  c === 'listo' ||
  c === 'sin_motivo' ||
  c === 'titular_solo' ||
  c === 'grupo_completo' ||
  // Se pueden corregir en la revisión cambiando el motivo: `motivo_invalido`
  // eligiendo uno habilitado, y `sin_fecha_fallecimiento` sacándolo de 37020
  // (si de verdad es un fallecimiento, la fecha va en el archivo).
  c === 'motivo_invalido' ||
  c === 'sin_fecha_fallecimiento' ||
  // La vigencia anterior al deceso también se arregla acá (es una de las dos
  // fechas de la fila). Estaba fuera de la lista, así que la pantalla pedía
  // corregirla pero no dejaba tocarla: había que rehacer el archivo.
  c === 'vigencia_antes_fallecimiento';

/**
 * Una fila se puede confirmar solo si tiene motivo y, cuando es fallecimiento,
 * la fecha real del deceso. Sin esa fecha Binary registra la baja incompleta
 * (pedido del DBA 2026-08-04), así que se bloquea acá y también en el server.
 */
export function filaConfirmable(f: FilaRev): boolean {
  if (!esEditable(f.item.clasif) || !f.tb) return false;
  // El motivo tiene que ser uno HABILITADO. `motivo_invalido` es editable a
  // propósito (se corrige en la revisión eligiendo otro), pero si el operador
  // no lo corrige, `tb` sigue siendo el código deprecado del archivo. Mandarlo
  // hacía que el server rechazara el lote COMPLETO con 400 — se caían también
  // las filas buenas y no se daba de baja a nadie.
  if (!MOTIVOS.some((m) => m.codigo === f.tb)) return false;
  // Fallecimiento: lo único que hace falta es la fecha del deceso; la vigencia
  // se deriva de ella (`vigenciaEfectiva`), no se carga.
  if (f.tb === TB_FALLECIMIENTO) return !!f.fall;
  // La vigencia es obligatoria salvo para 36010, donde el server la fuerza al
  // 1° del mes próximo (lo que se cargue acá se ignora).
  return f.tb === TB_EMPRESA || !!f.vig;
}

/**
 * Vigencia de una baja por fallecimiento: el 1° del mes SIGUIENTE al deceso
 * (ISO → ISO). Regla de Medicus (02/10/2026): las vigencias son siempre 1° de
 * mes; deceso 20/09 → vigencia 01/10. La fecha del deceso viaja aparte. Espejo
 * de `vigenciaPorFallecimiento` del server, que es quien manda.
 */
export function vigenciaPorFallecimiento(fallIso: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(fallIso ?? '');
  if (!m) return '';
  const anio = Number(m[1]);
  const mes = Number(m[2]);
  if (mes < 1 || mes > 12) return '';
  const sig = mes === 12 ? { a: anio + 1, m: 1 } : { a: anio, m: mes + 1 };
  return `${sig.a}-${String(sig.m).padStart(2, '0')}-01`;
}

/** La vigencia que realmente se manda: la derivada para 37020, la cargada para el resto. */
export function vigenciaEfectiva(f: { tb: string; vig: string; fall: string }): string {
  return f.tb === TB_FALLECIMIENTO ? vigenciaPorFallecimiento(f.fall) : f.vig;
}

/**
 * Fila que el operador tiene que corregir antes de seguir: es editable acá pero
 * le falta un dato o lo tiene mal (sin motivo, motivo no habilitado, sin fecha
 * de fallecimiento, vigencia anterior al deceso).
 *
 * NO entran las repetidas del archivo ni las que directamente no se procesan
 * (ya de baja, no encontrada, titular con familia parcial, alertas): esas se
 * informan y se ignoran, no hay nada que corregir.
 */
export function necesitaCorreccion(f: FilaRev): boolean {
  return esEditable(f.item.clasif) && !filaConfirmable(f);
}

/** Por qué una fila editable todavía no se puede confirmar (para mostrarlo en la fila). */
export function motivoNoConfirmable(f: FilaRev): string {
  if (!f.tb || !MOTIVOS.some((m) => m.codigo === f.tb)) return 'Elegí un motivo';
  if (f.tb === TB_FALLECIMIENTO) return f.fall ? '' : 'Cargá la fecha de fallecimiento';
  if (f.tb !== TB_EMPRESA && !f.vig) return 'Cargá la vigencia';
  return '';
}

/**
 * Tipo de alerta de una fila, o `undefined` si no genera alerta. Cubre los dos
 * casos: la clasificación que de por sí es alerta, y la fila editable que quedó
 * con datos incompletos porque nadie la corrigió en la revisión.
 */
export function tipoAlertaDe(f: FilaRev): AlertaBajaTipo | undefined {
  // Tiene rearmado planificado: se resuelve en este lote, no queda pendiente de
  // gestión. Es la razón de ser del rearmado desde el portal — estos casos iban
  // todos a Alertas y volvían por el operador.
  if (f.rearmado) return undefined;
  const porClasif = CLASIF_A_TIPO_ALERTA[f.item.clasif];
  if (porClasif && !esEditable(f.item.clasif)) return porClasif;
  // Fila que NO se puede tocar acá y que no es un caso de alerta: repetida en
  // el archivo, ya de baja, no encontrada, titular con familia parcial. Se
  // ignoran sin más — no son un pendiente de gestión, así que ensuciarían
  // Alertas Bajas. OJO: sin este corte caían en los chequeos de abajo y una
  // fila repetida sin motivo terminaba registrada como 'sin_motivo'.
  if (!esEditable(f.item.clasif)) return undefined;
  if (filaConfirmable(f)) return undefined; // se corrigió: se da de baja
  if (porClasif) return porClasif;
  // Fila que ERA válida y quedó incompleta al editarla (ej. le borraron el motivo).
  if (!f.tb || !MOTIVOS.some((m) => m.codigo === f.tb)) return 'sin_motivo';
  if (f.tb === TB_FALLECIMIENTO && !f.fall) return 'sin_fecha_fallecimiento';
  return undefined;
}

export const alertable = (f: FilaRev): boolean => tipoAlertaDe(f) !== undefined;

/** Mensaje de la alerta: el del preview o, si es un dato incompleto, qué falta. */
export function mensajeAlertaDe(f: FilaRev): string {
  if (esEditable(f.item.clasif)) return motivoNoConfirmable(f) || (f.item.detalle ?? '');
  return f.item.detalle ?? '';
}

/** Chip por clasificación del preview (color + texto). */
export const CLASIF_CHIP: Record<
  PreviewBajaBinItem['clasif'],
  { label: string; color: string; bg: string }
> = {
  listo: { label: 'Se dará de baja', color: '#166534', bg: '#e2f3e8' },
  titular_solo: { label: 'Titular sin grupo · baja de legajo', color: '#166534', bg: '#e2f3e8' },
  grupo_completo: { label: 'Grupo completo · baja de legajo', color: '#166534', bg: '#e2f3e8' },
  sin_motivo: { label: 'Falta motivo', color: '#b45309', bg: '#f8ecd2' },
  motivo_invalido: { label: 'Motivo no habilitado', color: '#b91c1c', bg: '#f9e2e5' },
  sin_fecha_fallecimiento: {
    label: 'Falta fecha de fallecimiento',
    color: '#b45309',
    bg: '#f8ecd2',
  },
  vigencia_antes_fallecimiento: {
    label: 'Vigencia anterior al fallecimiento',
    color: '#b91c1c',
    bg: '#f9e2e5',
  },
  duplicado_archivo: { label: 'Repetida en el archivo', color: '#6b7280', bg: '#f1f3f5' },
  // Alertas (van a la sección "Alertas Bajas", no se procesan acá): violeta.
  plan_desconocido: {
    label: 'Plan sin determinar · a Alertas',
    color: '#6d28d9',
    bg: '#ede9fe',
  },
  fallecimiento_con_grupo: {
    label: 'Fallecido con grupo · rearmado · a Alertas',
    color: '#6d28d9',
    bg: '#ede9fe',
  },
  fallecimiento_manual: {
    label: 'Fallecimiento manual · a Alertas',
    color: '#6d28d9',
    bg: '#ede9fe',
  },
  // Violeta y no ámbar: si no se rearma, queda pendiente en Alertas. No es un
  // dato que el operador pueda corregir en la fila.
  titular: { label: 'Titular con familia · rearmado · a Alertas', color: '#6d28d9', bg: '#ede9fe' },
  ya_baja: { label: 'Ya de baja', color: '#6b7280', bg: '#f1f3f5' },
  no_activo: { label: 'No activo en Binary', color: '#6b7280', bg: '#f1f3f5' },
  no_encontrado: { label: 'No encontrado', color: '#b91c1c', bg: '#f9e2e5' },
  ambiguo: { label: 'DNI ambiguo', color: '#b91c1c', bg: '#f9e2e5' },
};

const VERDE = { color: '#166534', bg: '#e2f3e8' };
const AMBAR = { color: '#b45309', bg: '#f8ecd2' };
/** Clasificaciones que ya vienen listas del archivo (chip verde de entrada). */
const CLASIF_VERDE: Array<PreviewBajaBinItem['clasif']> = [
  'listo',
  'titular_solo',
  'grupo_completo',
];

/**
 * Chip que se muestra en la fila, según el estado ACTUAL — no según la
 * clasificación con la que llegó del archivo.
 *
 * Apenas se corrige el dato la fila pasa a verde, y si una fila que estaba bien
 * queda incompleta al editarla pasa a ámbar. Antes solo se cambiaba el texto a
 * "Se dará de baja" y el color seguía siendo el rojo del error, así que una fila
 * ya arreglada seguía leyéndose como un problema.
 */
export function chipDeFila(f: FilaRev): { label: string; color: string; bg: string } {
  if (f.rearmado) return { label: 'Grupo rearmado · se envía con el lote', ...VERDE };
  if (filaConfirmable(f)) {
    // Las que ya venían verdes conservan su texto, que es más preciso
    // ("Titular sin grupo · baja de legajo" vs. un genérico).
    if (CLASIF_VERDE.includes(f.item.clasif)) return CLASIF_CHIP[f.item.clasif];
    return {
      label: f.item.esTitular ? 'Titular · baja de legajo' : 'Se dará de baja',
      ...VERDE,
    };
  }
  // Pendiente de corrección: el chip dice QUÉ falta, en ámbar. Unifica el color
  // de todo lo corregible (antes 'motivo no habilitado' y 'vigencia anterior al
  // fallecimiento' eran rojas y el resto ámbar, sin criterio).
  if (necesitaCorreccion(f)) return { label: motivoNoConfirmable(f) || 'Falta un dato', ...AMBAR };
  return CLASIF_CHIP[f.item.clasif];
}

/**
 * Fila cuya baja necesita repartir antes al grupo que queda.
 *
 * Sale de `requiereRearmado`, que es una propiedad del item y no del `clasif`:
 * son dos caminos distintos que llegan al mismo lugar (el fallecimiento del
 * titular con grupo, y la baja por empresa de un titular cuya familia no viene
 * completa en el archivo).
 */
export const requiereRearmado = (f: FilaRev): boolean => !!f.item.requiereRearmado;

/** Filas con el reparto ya decidido: son las que viajan como rearmado en el lote. */
export const conRearmado = (f: FilaRev): boolean => !!f.rearmado;
